import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { useRecurring } from "../hooks/useRecurring";
import { useHuchas } from "../hooks/useHuchas";
import { usePendientes } from "../hooks/usePendientes";
import { useClientes } from "../hooks/useClientes";
import { useNavigate, useLocation } from "react-router-dom";
import { t } from "../i18n";
import { formatAmount } from "../config/currencies";
import { CircleCheck, TrendingDown, PiggyBank, Search, ArrowUpDown, Contact, Plus, Smartphone, TrendingUp, RefreshCw, Hourglass, ScanLine, Paperclip } from "lucide-react";
import { scanReceiptImage, uploadAttachment, normalizeReceiptImage } from "./receiptOcr";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const now = new Date();

// Campos personalizados (modo negocio): hasta 10, claves custom1..custom10.
const CUSTOM_FIELDS_MAX = 10;
const emptyCustom = () =>
  Object.fromEntries(Array.from({ length: CUSTOM_FIELDS_MAX }, (_, i) => [`custom${i + 1}`, ""]));

export default function Anadir() {
  const { profile, lang, currency, configLoading, user } = useAuth();
  const { addEntry, visibleEntries, isPro, FREE_LIMITS, thisMonthCount } = useEntries();
  const { addRecurrent } = useRecurring();
  const { huchas, aportar } = useHuchas();
  const { addPendiente } = usePendientes();
  const { clientes, addCliente } = useClientes();
  const navigate = useNavigate();
  const location = useLocation();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);

  const [type,      setType]      = useState("income");
  const [cat,       setCat]       = useState("");
  const [desc,      setDesc]      = useState("");
  const [payment,   setPayment]   = useState("");
  const [amount,    setAmount]    = useState("");
  const [month,     setMonth]     = useState(MONTHS[now.getMonth()]);
  const [year,      setYear]      = useState(String(now.getFullYear()));
  const [dt,        setDt]        = useState(
    `${String(now.getDate()).padStart(2,"0")}/${String(now.getMonth()+1).padStart(2,"0")}/${now.getFullYear()} ${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`
  );
  // Formato ISO ("YYYY-MM-DDTHH:mm") que espera el selector nativo
  // <input type="datetime-local"> — "dt" (arriba) sigue siendo el string
  // "DD/MM/AAAA HH:mm" que usa el resto del formulario al enviar, así que
  // no hace falta tocar esa parte; solo se sincronizan los dos al cambiar.
  const [dtLocal, setDtLocal] = useState(
    `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,"0")}-${String(now.getDate()).padStart(2,"0")}T${String(now.getHours()).padStart(2,"0")}:${String(now.getMinutes()).padStart(2,"0")}`
  );
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringDay, setRecurringDay] = useState("1");
  const [toHucha,    setToHucha]    = useState(false);
  const [toInvestment, setToInvestment] = useState(false);
  const [toPendiente, setToPendiente] = useState(false);
  const [pendienteDue, setPendienteDue] = useState("");
  const [pendienteRealizado, setPendienteRealizado] = useState("");
  const [huchaId,    setHuchaId]    = useState("");
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [loading,   setLoading]   = useState(false);

  // Escáner de tickets (ver parseReceiptText más arriba)
  const [scanning,    setScanning]    = useState(false);
  const [scanNotice,  setScanNotice]  = useState(null); // { error:boolean, text:string } | null
  const [rawScanText, setRawScanText] = useState("");
  const [showRawScan, setShowRawScan] = useState(false);
  const scanInputRef = useRef(null);

  // Guardar la foto del ticket (o cualquier otro archivo adjunto) — a
  // diferencia de leerlo, esto sí es una función Pro (ver isPro más abajo):
  // sube el archivo a Storage al guardar la entrada. "receiptSource" dice
  // de dónde viene ("scan" = del escáner de tickets, "manual" = adjuntado
  // a mano sin escanear nada), porque cada uno se muestra de forma distinta.
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptFileName, setReceiptFileName] = useState("");
  const [receiptSource, setReceiptSource] = useState(null); // "scan" | "manual" | null
  const [saveTicketChecked, setSaveTicketChecked] = useState(false);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [attachProcessing, setAttachProcessing] = useState(false);
  const attachInputRef = useRef(null);

  // Igual que con el escáner, se reduce el tamaño y se corrige la
  // orientación de la foto ANTES de guardarla — así un adjunto manual
  // hecho con la cámara (que puede pesar varios MB) no ocupa de más en
  // Storage. Los PDF se dejan tal cual (no son imágenes, no aplica).
  async function handleAttachFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    setAttachProcessing(true);
    try {
      const finalFile = isPdf ? file : await normalizeReceiptImage(file);
      setReceiptFile(finalFile);
      setReceiptFileName(file.name || (lang==="en" ? "Attachment" : "Adjunto"));
      setReceiptSource("manual");
    } finally {
      setAttachProcessing(false);
    }
  }

  // Aplica el resultado de un OCR (texto + importe/descripción detectados)
  // a los campos del formulario. La usan tanto el escaneo hecho aquí mismo
  // como el que se hace desde el botón rápido del Panel (llega por
  // location.state, ver el useEffect más abajo) — así la heurística y el
  // texto de aviso solo viven en un sitio.
  function applyScanSuccess({ text, amount: guessedAmount, desc: guessedDesc, blob }) {
    setRawScanText(text || "");
    setIsCombined(false);
    setType("expense");
    if (guessedDesc) setDesc(guessedDesc);
    if (guessedAmount) setAmount(guessedAmount);
    setNow();

    // La foto ya normalizada (ver receiptOcr.js) se deja preparada para
    // subirla, pero SIN marcar "Guardar ticket" — guardarla es un paso
    // aparte y explícito (y solo está disponible en el plan Pro).
    if (blob) {
      setReceiptFile(blob);
      setReceiptFileName(lang==="en" ? "Scanned receipt" : "Foto del ticket escaneado");
      setReceiptSource("scan");
      setSaveTicketChecked(false);
    }

    setScanNotice(guessedAmount
      ? { error:false, text: lang==="en"
          ? "Receipt read — check the amount and description before saving."
          : "Ticket leído — revisa el importe y la descripción antes de guardar." }
      : { error:true, text: lang==="en"
          ? "Couldn't find a clear total — check the detected text below and fill in the amount by hand."
          : "No he encontrado un total claro — revisa el texto detectado abajo y rellena el importe a mano." });
  }

  function applyScanFailure() {
    setScanNotice({ error:true, text: lang==="en"
      ? "Something went wrong reading the image. Try a clearer photo, or fill it in by hand."
      : "Algo falló al leer la imagen. Prueba con una foto más clara, o rellena a mano." });
  }

  // Si venimos del botón de escaneo rápido del Panel, ese ya hizo el OCR
  // (para poder llevarte directo aquí con los campos rellenos). Lo
  // aplicamos una vez al montar y limpiamos el estado de navegación para
  // que un "atrás" del navegador no lo vuelva a aplicar.
  useEffect(() => {
    if (location.state?.scanResult) {
      applyScanSuccess(location.state.scanResult);
      navigate(location.pathname, { replace: true, state: null });
    } else if (location.state?.scanError) {
      applyScanFailure();
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);

  async function handleScanFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setScanNotice(null);
    setRawScanText("");
    try {
      const result = await scanReceiptImage(file);
      applyScanSuccess(result);
    } catch (err) {
      console.error("Error al escanear el ticket:", err);
      applyScanFailure();
    } finally {
      setScanning(false);
      e.target.value = "";
    }
  }

  const cats = type==="income" ? (c.income_cats||[]) : (c.expense_cats||[]);
  const accounts = (c.accounts||[]).map(a=>a.name);
  const fmtAmt = n => formatAmount(n, currency);

  // Campos personalizados: solo aplican en modo negocio (profile.perfil),
  // y solo los que el usuario haya activado y nombrado en Ajustes.
  const isNegocio = profile?.perfil === "negocio";
  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];
  const [customValues, setCustomValues] = useState(emptyCustom);

  // Cliente vinculado a esta venta/compra (solo modo negocio) — es un
  // campo aparte de los "campos personalizados", porque enlaza con una
  // ficha real (useClientes), no con texto libre.
  const [clienteId, setClienteId] = useState("");
  // Buscador del contacto (en vez de un <select> con toda la lista, que
  // se hace incómodo de recorrer si hay muchos) — se escribe y filtra.
  const [clienteSearch, setClienteSearch] = useState("");
  const [clienteSearchOpen, setClienteSearchOpen] = useState(false);
  const clientesFiltrados = clienteSearch.trim()
    ? clientes.filter(cl => {
        const q = clienteSearch.trim().toLowerCase();
        return (cl.nombre||"").toLowerCase().includes(q)
            || (cl.telefono||"").toLowerCase().includes(q)
            || (cl.dni||"").toLowerCase().includes(q)
            || (cl.email||"").toLowerCase().includes(q);
      })
    : clientes;
  // "+ Nuevo cliente" en el propio selector: evita tener que ir a la
  // sección Clientes solo para dar de alta uno rápido. Solo pide el
  // nombre aquí — el resto de datos (DNI, dirección...) se completan
  // después desde la ficha, si hace falta.
  const [creatingCliente, setCreatingCliente] = useState(false);
  const [nuevoClienteNombre, setNuevoClienteNombre] = useState("");
  const [nuevoClienteTelefono, setNuevoClienteTelefono] = useState("");
  const [savingCliente, setSavingCliente] = useState(false);
  const contactPickerSupported = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window;

  // Compara solo los dígitos del teléfono — más fiable que el nombre,
  // porque dos personas pueden llamarse igual sin compartir número.
  const soloDigitos = s => (s || "").replace(/\D/g, "");

  async function handleImportarContactoRapido() {
    if (!contactPickerSupported) return;
    try {
      const picked = await navigator.contacts.select(["name", "tel"], { multiple: false });
      if (!picked || picked.length === 0) return;
      const p = picked[0];
      const nombre = (p.name && p.name[0]) || "";
      const telefono = (p.tel && p.tel[0]) || "";

      const normTel = soloDigitos(telefono);
      const existente = normTel ? clientes.find(cl => soloDigitos(cl.telefono) === normTel) : null;
      if (existente) {
        const usarExistente = window.confirm(
          lang==="en"
            ? `You already have a contact with this phone: "${existente.nombre}". Use that one instead of creating a new contact?`
            : `Ya tienes un contacto guardado con este teléfono: "${existente.nombre}". ¿Quieres usar ese contacto en vez de crear uno nuevo?`
        );
        if (usarExistente) {
          setClienteId(existente.id);
          setCreatingCliente(false);
          setNuevoClienteNombre(""); setNuevoClienteTelefono("");
          return;
        }
        // Si dice que no, se sigue y se crea uno nuevo con estos datos
      }
      if (!nombre) {
        // Sin nombre no hay nada que crear automáticamente — se deja
        // en los campos para que se complete a mano.
        setNuevoClienteNombre(nombre);
        setNuevoClienteTelefono(telefono);
        return;
      }
      // Importado desde la agenda del teléfono: se guarda ya como contacto
      // nuevo sin pedir una confirmación aparte. Antes solo se rellenaban
      // estos campos y hacía falta pulsar "Guardar" a mano — si a alguien
      // se le olvidaba (lo normal, si ya seguías rellenando el resto de la
      // entrada), el contacto nunca se creaba ni se enlazaba a la entrada
      // ni al pendiente, aunque pareciera que sí se había importado.
      await handleCrearClienteRapido(nombre, telefono);
    } catch (err) {
      console.warn("Selector de contactos cancelado:", err);
    }
  }

  async function handleCrearClienteRapido(nombreArg, telefonoArg) {
    // Importante: "nombreArg"/"telefonoArg" solo deben venir de la
    // importación de contactos (que sí pasa textos de verdad). Si esta
    // función se usa directamente como onClick de un botón (como pasaba
    // antes con el botón "Guardar" de aquí abajo), React entrega el
    // propio evento del click como primer argumento — no un texto — y
    // "eventoDeClick.trim()" no es una función: eso rompía la función
    // ANTES incluso de entrar en el try/catch, así que no se guardaba
    // nada, no saltaba ningún error, y el botón parecía no hacer nada en
    // absoluto. Por eso aquí solo se usan estos argumentos si de verdad
    // son texto; si no, se cae siempre al valor de los campos del
    // formulario, que es lo correcto al pulsar el botón.
    const nombreFinal = (typeof nombreArg === "string" ? nombreArg : nuevoClienteNombre).trim();
    const telefonoFinal = (typeof telefonoArg === "string" ? telefonoArg : nuevoClienteTelefono).trim();
    if (!nombreFinal) return;
    setError("");
    setSavingCliente(true);
    try {
      const newId = await addCliente({
        nombre: nombreFinal,
        ...(telefonoFinal ? { telefono: telefonoFinal } : {}),
      });
      setClienteId(newId);
      setCreatingCliente(false);
      setNuevoClienteNombre("");
      setNuevoClienteTelefono("");
    } catch (err) {
      setError(lang==="en" ? `Couldn't create contact: ${err.message||err}` : `No se pudo crear el contacto: ${err.message||err}`);
    } finally {
      setSavingCliente(false);
    }
  }

  // Sugerencias de autocompletado para los campos personalizados (ej.
  // Cliente, Matrícula) — se sacan de lo que ya se haya escrito antes en
  // esas mismas casillas en entradas anteriores, para no tener que volver
  // a escribir el mismo dato cada vez. Es autocompletado nativo del
  // navegador (datalist), no hace falta ninguna librería.
  const customFieldSuggestions = {};
  activeCustomFields.forEach(f => {
    const seen = new Set();
    (visibleEntries || []).forEach(e => {
      const val = e[f.key];
      if (val && val.trim()) seen.add(val.trim());
    });
    customFieldSuggestions[f.key] = Array.from(seen).sort();
  });

  // Igual, pero para la descripción — se repite mucho ("Compra en
  // Mercadona", "Factura cliente X"), así no hay que escribirlo cada vez.
  const descSuggestions = Array.from(
    new Set((visibleEntries || []).map(e => e.desc).filter(d => d && d.trim()).map(d => d.trim()))
  ).sort();

  // Operación combinada (solo modo negocio): una sola entrada con un
  // ingreso y un gasto asociados; el tipo y el importe neto se calculan
  // automáticamente y todo se guarda como UNA sola entrada en Firestore.
  const [isCombined,      setIsCombined]      = useState(false);
  const [incomeAmount,    setIncomeAmount]    = useState("");
  const [expenseAmount,   setExpenseAmount]   = useState("");
  const [expenseDesc,     setExpenseDesc]     = useState("");
  const combinedTotal = (parseFloat((incomeAmount||"0").replace(",",".")) || 0)
                       - (parseFloat((expenseAmount||"0").replace(",",".")) || 0);
  // Para la categoría de una operación combinada se usa la misma lista
  // desplegable, combinando ambas listas (ingreso + gasto) sin duplicados.
  const combinedCats = [...new Set([...(c.income_cats||[]), ...(c.expense_cats||[])])];

  // Entrada simple (tipo + importe + categoría, siempre visible) vs.
  // entrada detallada (todo lo demás, oculto hasta que se pida). La
  // operación combinada YA NO fuerza a abrir los detalles — sus propios
  // campos (categoría combinada, importes, descripciones) sustituyen a
  // los simples en el mismo sitio, así que "Entrada detallada" se puede
  // abrir o cerrar independientemente, sin que nada salte de posición.
  const [detailMode, setDetailMode] = useState(false);
  const showDetails = detailMode;

  function setNow() {
    const n = new Date();
    setDt(`${String(n.getDate()).padStart(2,"0")}/${String(n.getMonth()+1).padStart(2,"0")}/${n.getFullYear()} ${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`);
    setDtLocal(`${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,"0")}-${String(n.getDate()).padStart(2,"0")}T${String(n.getHours()).padStart(2,"0")}:${String(n.getMinutes()).padStart(2,"0")}`);
  }

  // Convierte lo que devuelve el selector nativo (YYYY-MM-DDTHH:mm) al
  // formato DD/MM/AAAA HH:mm que usa el resto del formulario al guardar.
  function handleDtLocalChange(value) {
    setDtLocal(value);
    if (!value) return;
    const [datePart, timePart] = value.split("T");
    const [y, m, d] = datePart.split("-");
    setDt(`${d}/${m}/${y} ${timePart || "00:00"}`);
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(""); setSuccess("");

    let parsedMonth = month; let parsedYear = parseInt(year);
    if (dt) {
      const parts = dt.match(/^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2})$/);
      if (!parts) { setError(T("add_err_date")); return; }
      parsedMonth = MONTHS[parseInt(parts[2])-1];
      parsedYear  = parseInt(parts[3]);
    }

    // Si se ha marcado "Guardar ticket" (o se ha adjuntado un archivo a
    // mano), se sube primero a Storage — es una función Pro; en el plan
    // gratuito el escaneo sigue funcionando igual para leer el importe y
    // la descripción, solo que la foto no se guarda en ningún sitio.
    let receiptPath = null;
    const wantsToSaveReceipt = isPro && receiptFile && (
      receiptSource === "manual" || (receiptSource === "scan" && saveTicketChecked)
    );
    if (wantsToSaveReceipt) {
      setUploadingReceipt(true);
      try {
        receiptPath = await uploadAttachment(receiptFile, user.uid);
      } catch (err) {
        console.error("Error al subir el ticket:", err);
        setUploadingReceipt(false);
        setError(lang==="en"
          ? "Couldn't save the receipt photo. You can try again, or save the entry without it."
          : "No se pudo guardar la foto del ticket. Puedes reintentarlo, o guardar la entrada sin ella.");
        return;
      }
      setUploadingReceipt(false);
    }

    // Operación combinada: ingreso + gasto en una sola entrada. El tipo
    // (income/expense) y el importe se calculan a partir del neto, para
    // que Panel, Gráficos, Presupuestos, etc. sigan sumando bien sin
    // necesitar ningún cambio: para el resto de la app es una entrada
    // normal más, solo que además guarda el desglose ingreso/gasto.
    if (isNegocio && isCombined) {
      const incAmt = parseFloat((incomeAmount||"0").replace(",","."))  || 0;
      const expAmt = parseFloat((expenseAmount||"0").replace(",",".")) || 0;
      if (incAmt <= 0 && expAmt <= 0) { setError(lang==="en" ? "Enter an income and/or an expense amount" : "Introduce un importe de ingreso y/o de gasto"); return; }

      const net = incAmt - expAmt;
      const combinedType = net >= 0 ? "income" : "expense";

      const customData = {};
      activeCustomFields.forEach(f => {
        if (customValues[f.key]) customData[f.key] = customValues[f.key];
      });

      setLoading(true);
      const res = await addEntry({
        type: combinedType, cat: cat||"General", desc, payment,
        amount: Math.abs(net),
        isCombined: true, incomeAmount: incAmt, expenseAmount: expAmt, expenseDesc,
        month: parsedMonth, year: parsedYear, datetime: dt,
        ...(clienteId ? { clienteId } : {}),
        ...(receiptPath ? { receiptPath } : {}),
        ...customData,
      });
      setLoading(false);
      if (res.error) { setError(res.error); return; }

      setSuccess(lang==="en" ? "Combined operation saved" : "Operación combinada guardada");
      setIncomeAmount(""); setExpenseAmount(""); setExpenseDesc(""); setDesc("");
      setCustomValues(emptyCustom());
      setClienteId(""); setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch("");
      setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); setSaveTicketChecked(false);
      setNow();
      setTimeout(()=>setSuccess(""), 3000);
      return;
    }

    const amt = parseFloat(amount.replace(",","."));
    if (!amt || amt<=0) { setError(T("add_err_amount")); return; }

    // Si el gasto se destina a una hucha, usamos aportar() en vez del flujo
    // normal: ese helper ya crea su propia entrada en el historial (con
    // categoría "🐷 Nombre de la hucha") y suma al saldo acumulado de la
    // hucha, así que no hace falta (ni se debe) llamar también a addEntry.
    // IMPORTANTE: si el usuario marca esta casilla pero no elige una hucha
    // concreta, se bloquea el envío con un aviso — antes esto pasaba en
    // silencio como una entrada normal, dando la sensación de que el dinero
    // "desaparecía" sin ir a ninguna hucha.
    if (type === "expense" && toHucha) {
      if (!huchaId) {
        setError(lang==="en" ? "Select which savings jar to send this to" : "Selecciona a qué hucha quieres destinar esto");
        return;
      }
      const hucha = huchas.find(h => h.id === huchaId);
      if (!hucha) { setError(lang==="en" ? "Select a savings jar" : "Selecciona una hucha"); return; }
      setLoading(true);
      await aportar(hucha, amt, addEntry);
      setLoading(false);
      setSuccess(lang==="en"
        ? `${fmtAmt(amt)} added to "${hucha.name}"`
        : `${fmtAmt(amt)} añadidos a "${hucha.name}"`);
      setAmount(""); setDesc(""); setToHucha(false); setHuchaId("");
      setCustomValues(emptyCustom());
      setClienteId(""); setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch("");
      setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); setSaveTicketChecked(false);
      setNow();
      setTimeout(()=>setSuccess(""), 3000);
      return;
    }

    // Esto NO se registra como una inversión ya ejecutada — es dinero que
    // el usuario aparta a mano como "fondo extra para invertir" ese mes,
    // por encima del % automático. Se resta del Saldo Disponible (es un
    // gasto real, sí sale de tu balance), pero en Inversiones se suma al
    // "Disponible para invertir" de ese mes, no al total ya invertido.
    if (type === "expense" && toInvestment) {
      setLoading(true);
      try {
        const customData = {};
        activeCustomFields.forEach(f => {
          if (customValues[f.key]) customData[f.key] = customValues[f.key];
        });
        const res = await addEntry({
          type: "expense",
          cat: cat || "General",
          desc: desc || (lang==="en" ? "Investment fund top-up" : "Fondo extra para invertir"),
          payment, amount: amt,
          month: parsedMonth, year: parsedYear, datetime: dt,
          isInvestmentFundTopUp: true,
          ...(receiptPath ? { receiptPath } : {}),
          ...customData,
        });
        if (res.error) { setError(res.error); setLoading(false); return; }
        setSuccess(lang==="en"
          ? `${fmtAmt(amt)} added to this month's investable fund`
          : `${fmtAmt(amt)} añadidos al fondo para invertir de este mes`);
        setAmount(""); setDesc(""); setToInvestment(false);
        setCustomValues(emptyCustom());
        setClienteId(""); setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch("");
      setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); setSaveTicketChecked(false);
        setNow();
        setTimeout(()=>setSuccess(""), 3000);
      } catch (err) {
        console.error("Error al destinar a Inversiones:", err);
        setError(lang==="en"
          ? `Something went wrong sending this to Investments: ${err.message || err}`
          : `Algo falló al destinar esto a Inversiones: ${err.message || err}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Enviar a Pendientes en vez de crear el movimiento ya realizado: no
    // afecta al Saldo Disponible ni al Historial hasta que se confirme el
    // pago/cobro desde la propia página de Pendientes. Funciona tanto para
    // ingresos como para gastos (a diferencia de hucha/inversión, que solo
    // aplican a gastos).
    if (toPendiente) {
      // "Ya cobrado/pagado (opcional)": si el usuario indica que ya se ha
      // realizado parte del importe, esa parte se registra como una entrada
      // real (afecta a Saldo Disponible/Historial) y solo el resto se manda
      // a Pendientes. Si se deja vacío/0, se comporta como antes: el importe
      // completo va a Pendientes y no se toca el Historial.
      const realizadoAmt = Math.max(0, parseFloat((pendienteRealizado||"0").replace(",", ".")) || 0);
      if (realizadoAmt > amt) {
        setError(lang==="en"
          ? "The amount already received/paid can't be higher than the total"
          : "Lo ya cobrado/pagado no puede ser mayor que el importe total");
        return;
      }
      const restanteAmt = Math.max(0, amt - realizadoAmt);

      setLoading(true);
      try {
        const customData = {};
        activeCustomFields.forEach(f => {
          if (customValues[f.key]) customData[f.key] = customValues[f.key];
        });
        const dueDate = pendienteDue || null;
        const descTrim = desc.trim() || (type==="income"
          ? (lang==="en" ? "Pending income" : "Pendiente de ingreso")
          : (lang==="en" ? "Pending expense" : "Pendiente de gasto"));

        if (realizadoAmt > 0) {
          const res = await addEntry({
            type, cat: cat || "General", desc: descTrim, payment, amount: realizadoAmt,
            month: parsedMonth, year: parsedYear, datetime: dt,
            ...(clienteId ? { clienteId } : {}),
            ...(receiptPath ? { receiptPath } : {}),
            ...customData,
          });
          if (res.error) { setError(res.error); setLoading(false); return; }
        }
        if (restanteAmt > 0) {
          await addPendiente({
            type,
            desc: descTrim,
            cat: cat || "General",
            payment,
            amount: restanteAmt,
            due: dueDate,
            ...(clienteId ? { clienteId } : {}),
            ...(receiptPath ? { receiptPath } : {}),
            ...customData,
          });
        }

        if (realizadoAmt > 0 && restanteAmt > 0) {
          setSuccess(lang==="en"
            ? `${fmtAmt(realizadoAmt)} added to History, ${fmtAmt(restanteAmt)} sent to Pending`
            : `${fmtAmt(realizadoAmt)} añadidos al Historial, ${fmtAmt(restanteAmt)} enviados a Pendientes`);
        } else if (restanteAmt > 0) {
          setSuccess(type==="income"
            ? (lang==="en" ? `Sent to Pending as income` : `Enviado a Pendientes como pendiente de ingreso`)
            : (lang==="en" ? `Sent to Pending as expense` : `Enviado a Pendientes como pendiente de gasto`));
        } else {
          setSuccess(lang==="en" ? "Entry saved, nothing left pending" : "Entrada guardada, no queda nada pendiente");
        }
        setAmount(""); setDesc(""); setToPendiente(false); setPendienteDue(""); setPendienteRealizado("");
        setCustomValues(emptyCustom());
        setClienteId(""); setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch("");
      setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); setSaveTicketChecked(false);
        setNow();
        setTimeout(()=>setSuccess(""), 3000);
      } catch (err) {
        console.error("Error al enviar a Pendientes:", err);
        setError(lang==="en"
          ? `Something went wrong sending this to Pending: ${err.message || err}`
          : `Algo falló al enviar esto a Pendientes: ${err.message || err}`);
      } finally {
        setLoading(false);
      }
      return;
    }

    // Solo se incluyen los campos activos que el usuario haya rellenado
    const customData = {};
    activeCustomFields.forEach(f => {
      if (customValues[f.key]) customData[f.key] = customValues[f.key];
    });

    setLoading(true);
    const res = await addEntry({
      type, cat: cat||"General", desc, payment, amount: amt,
      month: parsedMonth, year: parsedYear, datetime: dt,
      ...(clienteId ? { clienteId } : {}),
      ...(receiptPath ? { receiptPath } : {}),
      ...customData,
    });

    if (res.error) { setError(res.error); setLoading(false); return; }

    // Si es recurrente, guárdalo también en recurrents
    if (isRecurring) {
      await addRecurrent({
        type,
        cat: cat || "General",
        desc,
        payment,
        amount: amt,
        day: parseInt(recurringDay) || 1,
      });
    }

    setLoading(false);
    setSuccess(isRecurring
      ? (lang==="en" ? "Entry saved and added as recurring" : "Entrada guardada y añadida como recurrente")
      : T("add_success")
    );
    setAmount(""); setDesc(""); setIsRecurring(false);
    setCustomValues(emptyCustom());
    setClienteId(""); setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch("");
    setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); setSaveTicketChecked(false);
    setNow();
    setTimeout(()=>setSuccess(""), 3000);
  }

  const usedPct = Math.min(100, Math.round((thisMonthCount/FREE_LIMITS.entriesPerMonth)*100));

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><Plus size={22}/> {T("add_title")}</h2>
          <p className="page-sub">{T("add_sub")} {c.income_label?.toLowerCase()||T("income_label").toLowerCase()} {T("add_sub2")} {c.expense_label?.toLowerCase()||T("expense_label").toLowerCase()}</p>
        </div>
      </div>

      {!isPro && (
        <div className="card card-sm" style={{marginBottom:20,maxWidth:480}}>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:13,color:"var(--gray-600)",marginBottom:6}}>
            <span>{T("add_entries_month")} <strong>{thisMonthCount} / {FREE_LIMITS.entriesPerMonth}</strong></span>
            {usedPct>=80 && <button className="btn btn-pro btn-sm" onClick={()=>navigate("/pro")}>{T("add_expand")}</button>}
          </div>
          <div className="limit-bar">
            <div className={`limit-fill ${usedPct>=100?"full":usedPct>=80?"warn":""}`} style={{width:`${usedPct}%`}}/>
          </div>
        </div>
      )}

      <div className="card" style={{maxWidth:520}}>
        {error   && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success" style={{display:"flex",alignItems:"center",gap:6}}><CircleCheck size={15}/> {success}</div>}

        <form onSubmit={handleSubmit}>
          {/* Escáner de tickets — OCR gratuito en el navegador (Tesseract.js).
              Fuera de "detallada" a propósito: es justo el atajo para no
              tener que rellenar nada a mano, así que tiene que verse desde
              el primer momento, no escondido tras un botón de "más detalles". */}
          <div className="form-group">
            <input ref={scanInputRef} type="file" accept="image/*" capture="environment"
                   style={{display:"none"}} onChange={handleScanFile} />
            <button type="button" className="btn btn-outline" disabled={scanning}
                    style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6}}
                    onClick={()=>scanInputRef.current?.click()}>
              <ScanLine size={16}/> {scanning
                ? (lang==="en" ? "Reading receipt..." : "Leyendo ticket...")
                : (lang==="en" ? "Scan receipt" : "Escanear ticket")}
            </button>
            {scanNotice && (
              <p className="form-hint" style={{marginTop:6, color: scanNotice.error ? "var(--red)" : "var(--green)"}}>
                {scanNotice.text}
              </p>
            )}
            {rawScanText && (
              <div style={{marginTop:6}}>
                <button type="button" onClick={()=>setShowRawScan(v=>!v)}
                        style={{background:"none",border:"none",color:"var(--blue)",fontSize:12,cursor:"pointer",padding:0}}>
                  {showRawScan
                    ? (lang==="en" ? "Hide detected text" : "Ocultar texto detectado")
                    : (lang==="en" ? "Show detected text" : "Ver texto detectado")}
                </button>
                {showRawScan && (
                  <pre style={{marginTop:6, fontSize:11, whiteSpace:"pre-wrap", background:"var(--gray-50)",
                                border:"1px solid var(--border)", borderRadius:8, padding:10,
                                maxHeight:160, overflowY:"auto"}}>
                    {rawScanText}
                  </pre>
                )}
              </div>
            )}
            {/* Guardar la foto ya leída — a diferencia del escaneo (gratis
                para todos), guardar la foto es una función Pro. */}
            {receiptFile && receiptSource === "scan" && (
              isPro ? (
                <label style={{display:"flex",alignItems:"center",gap:8,marginTop:8,cursor:"pointer"}}>
                  <input type="checkbox" checked={saveTicketChecked}
                         onChange={e=>setSaveTicketChecked(e.target.checked)}
                         style={{width:16,height:16,cursor:"pointer"}}/>
                  <span style={{fontSize:13,color:"var(--gray-700)"}}>
                    {lang==="en" ? "Save ticket" : "Guardar ticket"}
                  </span>
                </label>
              ) : (
                <p className="form-hint" style={{marginTop:8}}>
                  {lang==="en" ? "Saving the ticket photo is a Pro feature — " : "Guardar la foto del ticket es una función Pro — "}
                  <span style={{color:"var(--blue)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/pro")}>
                    {lang==="en" ? "see Pro" : "ver Pro"}
                  </span>
                </p>
              )
            )}
          </div>

          {/* Adjuntar foto o documento — función Pro, independiente del
              escáner: sirve para guardar cualquier justificante en una
              entrada, aunque no se use el escaneo de ticket. Arriba del
              todo, junto al escáner, para que se vea desde el principio
              y no quede escondida entre el resto de campos. */}
          <div className="form-group">
            <label className="form-label">
              <Paperclip size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:4}}/> {lang==="en" ? "Attach photo or file (optional)" : "Adjuntar foto o documento (opcional)"}
            </label>
            {!isPro ? (
              <p className="form-hint">
                {lang==="en" ? "Attaching files to an entry is a Pro feature — " : "Adjuntar archivos a una entrada es una función Pro — "}
                <span style={{color:"var(--blue)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/pro")}>
                  {lang==="en" ? "see Pro" : "ver Pro"}
                </span>
              </p>
            ) : receiptFile && receiptSource === "manual" ? (
              <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                           padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                <span style={{fontSize:13, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {receiptFileName}
                </span>
                <button type="button" className="btn btn-outline btn-sm"
                        onClick={()=>{ setReceiptFile(null); setReceiptFileName(""); setReceiptSource(null); }}>
                  {lang==="en" ? "Remove" : "Quitar"}
                </button>
              </div>
            ) : (
              <>
                <input ref={attachInputRef} type="file" accept="image/*,application/pdf"
                       style={{display:"none"}} onChange={handleAttachFile} />
                <button type="button" className="btn btn-outline" disabled={attachProcessing}
                        style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6}}
                        onClick={()=>attachInputRef.current?.click()}>
                  <Paperclip size={16}/> {attachProcessing
                    ? (lang==="en" ? "Processing..." : "Procesando...")
                    : (lang==="en" ? "Attach" : "Adjuntar")}
                </button>
              </>
            )}
          </div>

          {/* Entrada simple: tipo + categoría + importe, siempre visibles.
              Todo lo demás (fecha, cuenta, adjuntos, contacto, recurrente,
              pendientes, hucha, inversión...) vive en "Entrada detallada",
              plegado por defecto. La "Operación combinada" (solo modo
              negocio) se muestra aquí, en una posición FIJA que no se
              mueve nunca — al marcarla, los campos de justo debajo
              cambian de normal a combinado en su sitio, sin que salte ni
              se abra de golpe el resto de "Entrada detallada". */}
          {isNegocio && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={isCombined}
                       onChange={e=>{ setIsCombined(e.target.checked); setToHucha(false); setIsRecurring(false); }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                  <ArrowUpDown size={14} style={{marginRight:2,display:"inline",verticalAlign:"-2px"}}/> {lang==="en" ? "Combined operation (income + expense together)" : "Operación combinada (ingreso + gasto juntos)"}
                </span>
              </label>
              {isCombined && (
                <p className="form-hint">
                  {lang==="en"
                    ? "E.g. you sold something and want to log the sale price and the cost together, as a single operation showing the net total."
                    : "Ej: vendiste algo y quieres registrar el precio de venta y el coste juntos, como una sola operación con el total neto."}
                </p>
              )}
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{T("add_type")}</label>
            <div style={{display:"flex",gap:10}}>
              {[["income",(c.income_label||T("income_label")),CircleCheck],["expense",(c.expense_label||T("expense_label")),TrendingDown]].map(([v,l,Icon])=>(
                <label key={v} style={{flex:1,cursor:"pointer"}}>
                  <input type="radio" value={v} checked={type===v} onChange={()=>{setType(v);setCat("");}} style={{display:"none"}}/>
                  <div style={{padding:"10px 14px",borderRadius:"var(--radius-sm)",textAlign:"center",
                               border:`2px solid ${type===v?"var(--blue)":"var(--border)"}`,
                               background:type===v?"var(--blue-l)":"white",fontSize:14,fontWeight:500,
                               color:type===v?"var(--blue)":"var(--gray-700)",display:"flex",alignItems:"center",justifyContent:"center",gap:6}}>
                    <Icon size={15}/> {l}
                  </div>
                </label>
              ))}
            </div>
          </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{T("add_cat")}</label>
            {configLoading ? (
              <select className="form-select" disabled>
                <option>{lang==="en" ? "Loading categories..." : "Cargando categorías..."}</option>
              </select>
            ) : (
              <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
                <option value="">{T("add_cat_placeholder")}</option>
                {cats.map(c=><option key={c}>{c}</option>)}
                <option value="Otro">{T("add_other")}</option>
              </select>
            )}
          </div>
          )}

          {/* Misma "casilla" que la Categoría de arriba, solo que con la
              lista combinada (ingreso+gasto) — ocupa el mismo lugar, así
              que al marcar/desmarcar "Operación combinada" no hay salto:
              una sustituye a la otra en el mismo sitio. */}
          {isCombined && !configLoading && (
            <div className="form-group">
              <label className="form-label">{T("add_cat")}</label>
              <select className="form-select" value={cat} onChange={e=>setCat(e.target.value)}>
                <option value="">{T("add_cat_placeholder")}</option>
                {combinedCats.map(cc=><option key={cc}>{cc}</option>)}
                <option value="Otro">{T("add_other")}</option>
              </select>
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{T("add_desc")} <span style={{color:"var(--gray-400)",fontWeight:400}}>{T("add_desc_optional")}</span></label>
            <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                   placeholder={T("add_desc_placeholder")} list="suggestions-desc" />
            <datalist id="suggestions-desc">
              {descSuggestions.map(val => <option key={val} value={val} />)}
            </datalist>
          </div>
          )}

          {/* Sustituye a Descripción + Importe (de aquí abajo) cuando la
              operación es combinada — mismo sitio, mismo orden visual. */}
          {isCombined && (
            <div className="form-group">
              <div className="form-row" style={{display:"flex",gap:10}}>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><CircleCheck size={13}/> {lang==="en" ? "Income amount" : "Importe Ingreso"}</label>
                  <input className="form-input" type="number" step="0.01" min="0"
                         value={incomeAmount} onChange={e=>setIncomeAmount(e.target.value)} placeholder="0.00" />
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><TrendingDown size={13}/> {lang==="en" ? "Expense amount" : "Importe Gasto"}</label>
                  <input className="form-input" type="number" step="0.01" min="0"
                         value={expenseAmount} onChange={e=>setExpenseAmount(e.target.value)} placeholder="0.00" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><CircleCheck size={13}/> {lang==="en" ? "Income description" : "Descripción del ingreso"} <span style={{color:"var(--gray-400)",fontWeight:400}}>{T("add_desc_optional")}</span></label>
                <input className="form-input" value={desc} onChange={e=>setDesc(e.target.value)}
                       placeholder={lang==="en" ? "E.g. sale to customer..." : "Ej: venta a cliente..."} />
              </div>

              <div className="form-group" style={{marginBottom:0}}>
                <label className="form-label" style={{display:"flex",alignItems:"center",gap:5}}><TrendingDown size={13}/> {lang==="en" ? "Expense description" : "Descripción del gasto"} <span style={{color:"var(--gray-400)",fontWeight:400}}>{T("add_desc_optional")}</span></label>
                <input className="form-input" value={expenseDesc} onChange={e=>setExpenseDesc(e.target.value)}
                       placeholder={lang==="en" ? "E.g. materials, fuel..." : "Ej: material, gasolina..."} />
              </div>

              <div style={{marginTop:8,padding:"10px 14px",borderRadius:"var(--radius-sm)",
                           background:combinedTotal>=0?"var(--green-l,#f0fdf4)":"var(--red-l)",
                           border:`1px solid ${combinedTotal>=0?"var(--green)":"var(--red)"}`,
                           fontSize:14,fontWeight:700,color:combinedTotal>=0?"var(--green)":"var(--red)"}}>
                {lang==="en" ? "Net total" : "Total neto"}: {combinedTotal>=0?"+":""}{fmtAmt(combinedTotal)}
              </div>
            </div>
          )}

          {!isCombined && (
          <div className="form-group">
            <label className="form-label">{T("add_amount")}</label>
            <input className="form-input" type="number" step="0.01" min="0.01"
                   value={amount} onChange={e=>setAmount(e.target.value)} placeholder="0.00" required />
          </div>
          )}

          <div className="form-group" style={{marginTop:6, marginBottom:showDetails?18:6}}>
            <button type="button" onClick={()=>setDetailMode(v=>!v)}
                    className="btn btn-outline"
                    style={{width:"100%",justifyContent:"center",display:"flex",alignItems:"center",gap:6,padding:"10px 14px"}}>
              <Plus size={16} style={{transform:showDetails?"rotate(45deg)":"none",transition:"transform .15s"}}/>
              {showDetails
                ? (lang==="en" ? "Hide details" : "Ocultar detalles")
                : (lang==="en" ? "Add details (date, contact, extras...)" : "Añadir detalles (fecha, contacto, extras...)")}
            </button>
          </div>

          {showDetails && (
          <>

          {accounts.length > 0 && (
            <div className="form-group">
              <label className="form-label">{T("add_account")}</label>
              <select className="form-select" value={payment} onChange={e=>setPayment(e.target.value)}>
                <option value="">{T("add_no_account")}</option>
                {accounts.map(a=><option key={a}>{a}</option>)}
              </select>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">{T("add_datetime")}</label>
            <input className="form-input" type="datetime-local" value={dtLocal}
                   onChange={e=>handleDtLocalChange(e.target.value)}
                   style={{
                     width:"100%", boxSizing:"border-box", display:"block",
                     WebkitAppearance:"none", appearance:"none",
                     minHeight:44, lineHeight:"normal",
                   }} />
          </div>

          {/* Cliente vinculado (solo modo negocio) — al elegir uno, al
              entrar en su ficha en Contactos se verá este movimiento en su
              historial de compras/ventas. */}
          {isNegocio && (
            <div className="form-group">
              <label className="form-label">
                <Contact size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:4}}/> {lang==="en" ? "Contact (optional)" : "Contacto (opcional)"}
              </label>
              {clienteId && !creatingCliente ? (
                // Contacto ya elegido: se muestra como chip, con opción de cambiarlo
                <div style={{display:"flex", alignItems:"center", justifyContent:"space-between",
                             padding:"10px 12px", background:"var(--gray-50)", borderRadius:8}}>
                  <span style={{fontWeight:600, fontSize:14}}>
                    {clientes.find(cl => cl.id === clienteId)?.nombre || "…"}
                  </span>
                  <button type="button" className="btn btn-outline btn-sm" onClick={()=>{ setClienteId(""); setClienteSearch(""); }}>
                    {lang==="en" ? "Change" : "Cambiar"}
                  </button>
                </div>
              ) : !creatingCliente ? (
                <div style={{position:"relative"}}>
                  <div style={{display:"flex", gap:8}}>
                    <input className="form-input" style={{flex:1}} value={clienteSearch}
                           onChange={e=>{ setClienteSearch(e.target.value); setClienteSearchOpen(true); }}
                           onFocus={()=>setClienteSearchOpen(true)}
                           placeholder={lang==="en" ? "Search contact by name..." : "Buscar contacto por nombre..."}/>
                    {/* Acceso directo: crear contacto nuevo sin tener que buscar
                        primero ni escribir nada — antes solo aparecía dentro del
                        desplegable, ahora también hay un botón siempre a la vista. */}
                    <button type="button" className="btn btn-outline" title={lang==="en" ? "New contact" : "Nuevo contacto"}
                            aria-label={lang==="en" ? "New contact" : "Nuevo contacto"}
                            style={{width:40,height:40,padding:0,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}
                            onClick={()=>{ setCreatingCliente(true); setClienteSearchOpen(false); setClienteSearch(""); }}>
                      <Plus size={18}/>
                    </button>
                  </div>
                  {clienteSearchOpen && (
                    <>
                      <div style={{position:"fixed", inset:0, zIndex:19}} onClick={()=>setClienteSearchOpen(false)} />
                      <div style={{position:"absolute", top:"100%", left:0, right:0, marginTop:4, zIndex:20,
                                   background:"var(--card,#fff)", border:"1px solid var(--border)", borderRadius:8,
                                   maxHeight:220, overflowY:"auto", boxShadow:"var(--shadow-md,0 6px 20px rgba(0,0,0,.15))"}}>
                        {clientesFiltrados.length === 0 ? (
                          <div style={{padding:"10px 12px", fontSize:13, color:"var(--gray-400)"}}>
                            {clientes.length===0
                              ? (lang==="en" ? "No contacts yet" : "Todavía no tienes contactos")
                              : (lang==="en" ? "No matches" : "Sin resultados")}
                          </div>
                        ) : clientesFiltrados.slice(0, 8).map(cl => (
                          <div key={cl.id}
                               onClick={()=>{ setClienteId(cl.id); setClienteSearch(""); setClienteSearchOpen(false); }}
                               style={{padding:"9px 12px", cursor:"pointer", fontSize:14}}
                               onMouseEnter={e=>e.currentTarget.style.background="var(--gray-50)"}
                               onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                            {cl.nombre}
                          </div>
                        ))}
                        <div onClick={()=>{ setCreatingCliente(true); setClienteSearchOpen(false); }}
                             style={{padding:"9px 12px", cursor:"pointer", fontSize:14, fontWeight:700,
                                     color:"var(--gold-dark,#9C7A16)",
                                     borderTop: clientesFiltrados.length>0 ? "1px solid var(--border)" : "none"}}>
                          + {lang==="en" ? "New contact..." : "Nuevo contacto..."}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{display:"flex", flexDirection:"column", gap:8}}>
                  {contactPickerSupported && (
                    <button type="button" className="btn btn-outline btn-sm" style={{justifyContent:"center"}}
                            onClick={handleImportarContactoRapido}>
                      <Smartphone size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en" ? "Import from my contacts" : "Importar desde mis contactos"}
                    </button>
                  )}
                  <div style={{display:"flex", gap:8}}>
                    <input className="form-input" autoFocus value={nuevoClienteNombre}
                           onChange={e=>setNuevoClienteNombre(e.target.value)}
                           placeholder={lang==="en" ? "New contact's name" : "Nombre del contacto nuevo"}
                           onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); handleCrearClienteRapido(); } }}/>
                    <input className="form-input" type="tel" style={{maxWidth:130}} value={nuevoClienteTelefono}
                           onChange={e=>setNuevoClienteTelefono(e.target.value)}
                           placeholder={lang==="en" ? "Phone" : "Teléfono"}
                           onKeyDown={e=>{ if(e.key==="Enter"){ e.preventDefault(); handleCrearClienteRapido(); } }}/>
                  </div>
                  <div style={{display:"flex", gap:8}}>
                    <button type="button" className="btn btn-primary btn-sm" style={{flex:1,justifyContent:"center"}}
                            disabled={savingCliente || !nuevoClienteNombre.trim()} onClick={()=>handleCrearClienteRapido()}>
                      {savingCliente ? "..." : (lang==="en" ? "Save" : "Guardar")}
                    </button>
                    <button type="button" className="btn btn-outline btn-sm" style={{flex:1,justifyContent:"center"}}
                            onClick={()=>{ setCreatingCliente(false); setNuevoClienteNombre(""); setNuevoClienteTelefono(""); setClienteSearch(""); }}>
                      {lang==="en" ? "Cancel" : "Cancelar"}
                    </button>
                  </div>
                  {/* Si falla el guardado (p.ej. sin conexión o un problema de
                      permisos), se avisa aquí mismo, junto al botón — antes el
                      único aviso estaba arriba del todo del formulario, y con
                      "Adjuntar" y el resto de campos por encima, quedaba fuera
                      de la vista y parecía que el botón no había hecho nada. */}
                  {error && (
                    <p style={{fontSize:12,color:"var(--red)",margin:0}}>{error}</p>
                  )}
                </div>
              )}
              {creatingCliente && (
                <p className="form-hint">
                  {lang==="en"
                    ? "Only the name is needed now — add DNI, address, etc. later from the Contacts section."
                    : "Solo hace falta el nombre ahora — añade DNI, dirección, etc. después desde la sección Contactos."}
                </p>
              )}
            </div>
          )}

          {/* Campos personalizados (solo modo negocio, y si hay alguno activado) */}
          {activeCustomFields.length > 0 && (
            <div className="form-group">
              <label className="form-label">{lang==="en" ? "Extra details" : "Datos adicionales"}</label>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {activeCustomFields.map(f => (
                  <React.Fragment key={f.key}>
                    <input className="form-input"
                           placeholder={f.name}
                           list={`suggestions-${f.key}`}
                           value={customValues[f.key]}
                           onChange={e=>setCustomValues(v=>({...v,[f.key]:e.target.value}))}/>
                    <datalist id={`suggestions-${f.key}`}>
                      {(customFieldSuggestions[f.key]||[]).map(val => (
                        <option key={val} value={val} />
                      ))}
                    </datalist>
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          {/* Destinar a una hucha (solo en gastos, y si hay alguna hucha creada) */}
          {!isCombined && type === "expense" && huchas.length > 0 && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={toHucha}
                       onChange={e=>{ setToHucha(e.target.checked); if(!e.target.checked) setHuchaId(""); if(e.target.checked) { setToInvestment(false); setToPendiente(false); } }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                  <PiggyBank size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en" ? "Send to a savings jar?" : "¿Destinar a una hucha?"}
                </span>
              </label>
              {toHucha && (
                <select className="form-select" value={huchaId} onChange={e=>setHuchaId(e.target.value)}
                        style={{marginTop:8}}>
                  <option value="">{lang==="en"?"Select a jar":"Selecciona una hucha"}</option>
                  {huchas.map(h => (
                    <option key={h.id} value={h.id}>{h.icon} {h.name}</option>
                  ))}
                </select>
              )}
              {toHucha && (
                <p className="form-hint">
                  {lang==="en"
                    ? "This will be recorded as a contribution to the jar instead of a regular expense category."
                    : "Esto se registrará como un aporte a la hucha en vez de en una categoría de gasto normal."}
                </p>
              )}
            </div>
          )}

          {/* Destinar a inversión (solo en gastos) */}
          {!isCombined && type === "expense" && !toHucha && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={toInvestment}
                       onChange={e=>{ setToInvestment(e.target.checked); if(e.target.checked) setToPendiente(false); }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                  <TrendingUp size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en" ? "Send to Investments?" : "¿Destinar a inversión?"}
                </span>
              </label>
              {toInvestment && (
                <p className="form-hint">
                  {lang==="en"
                    ? "This will be subtracted from your available balance, and added to this month's investable fund in Investments (not yet counted as an executed investment)."
                    : "Esto se restará de tu Saldo Disponible, y se sumará al fondo para invertir de este mes en Inversiones (todavía no cuenta como una inversión ejecutada)."}
                </p>
              )}
            </div>
          )}

          {/* Enviar a Pendientes (ingresos o gastos, si no se destina ya a hucha/inversión) */}
          {!isCombined && !toHucha && !toInvestment && (
            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                <input type="checkbox" checked={toPendiente}
                       onChange={e=>{ setToPendiente(e.target.checked); if(!e.target.checked) { setPendienteDue(""); setPendienteRealizado(""); } }}
                       style={{width:18,height:18,cursor:"pointer"}}/>
                <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)",display:"inline-flex",alignItems:"center",gap:5}}>
                  <Hourglass size={15}/> {type==="income"
                    ? (lang==="en" ? "Send to Pending income?" : "¿Destinar a pendiente de ingreso?")
                    : (lang==="en" ? "Send to Pending expense?" : "¿Destinar a pendiente de gasto?")}
                </span>
              </label>
              {toPendiente && (
                <>
                  <p className="form-hint">
                    {lang==="en"
                      ? "This won't affect your available balance or History until you confirm it as paid/received from the Pending page."
                      : "Esto no afectará a tu Saldo Disponible ni al Historial hasta que lo confirmes como pagado/cobrado desde la página de Pendientes."}
                  </p>
                  <div style={{marginTop:10}}>
                    <label className="form-label">
                      {type==="income"
                        ? (lang==="en" ? "Already received (optional)" : "Ya cobrado (opcional)")
                        : (lang==="en" ? "Already paid (optional)" : "Ya pagado (opcional)")}
                    </label>
                    <input className="form-input" type="number" step="0.01" min="0"
                           value={pendienteRealizado} onChange={e=>setPendienteRealizado(e.target.value)}
                           placeholder="0.00"/>
                    {(() => {
                      const realizadoNum = Math.max(0, parseFloat((pendienteRealizado||"0").replace(",", ".")) || 0);
                      const amtNum = parseFloat((amount||"0").replace(",", ".")) || 0;
                      if (realizadoNum <= 0) return null;
                      const restanteNum = Math.max(0, amtNum - realizadoNum);
                      return (
                        <p className="form-hint">
                          {lang==="en"
                            ? `${fmtAmt(realizadoNum)} will be added to History now, ${fmtAmt(restanteNum)} stays pending.`
                            : `${fmtAmt(realizadoNum)} se añadirán ya al Historial, ${fmtAmt(restanteNum)} quedan pendientes.`}
                        </p>
                      );
                    })()}
                  </div>
                  <div style={{marginTop:10}}>
                    <label className="form-label">
                      {lang==="en" ? "Due date (optional)" : "Fecha de vencimiento (opcional)"}
                    </label>
                    <input className="form-input" type="date" value={pendienteDue}
                           onChange={e=>setPendienteDue(e.target.value)}/>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Recurrente (no aplica si el gasto se destina a una hucha, ni en operación combinada) */}
          {!toHucha && !toInvestment && !toPendiente && !isCombined && (
          <div className="form-group">
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
              <input type="checkbox" checked={isRecurring} onChange={e=>setIsRecurring(e.target.checked)}
                     style={{width:18,height:18,cursor:"pointer"}}/>
              <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                <RefreshCw size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en" ? "Save as recurring (monthly)" : "Guardar como recurrente (mensual)"}
              </span>
            </label>
          </div>
          )}

          {!toHucha && !toInvestment && !toPendiente && !isCombined && isRecurring && (
            <div className="form-group" style={{maxWidth:200,marginTop:-8}}>
              <label className="form-label">
                {lang==="en" ? "Day of month" : "Día del mes"}
              </label>
              <select className="form-select" value={recurringDay} onChange={e=>setRecurringDay(e.target.value)}>
                {Array.from({length:28},(_,i)=>i+1).map(d=>(
                  <option key={d} value={d}>{lang==="en"?`Day ${d}`:`Día ${d}`}</option>
                ))}
              </select>
              <p className="form-hint">
                {lang==="en"
                  ? "This entry will be added automatically on this day each month"
                  : "Esta entrada se añadirá automáticamente este día cada mes"}
              </p>
            </div>
          )}
          </>
          )}

          <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"12px"}}
                  type="submit" disabled={loading||configLoading||uploadingReceipt}>
            {configLoading ? (lang==="en"?"Loading...":"Cargando...")
              : uploadingReceipt ? (lang==="en"?"Saving receipt...":"Guardando ticket...")
              : loading ? T("add_saving") : T("add_btn")}
          </button>
        </form>
      </div>
    </div>
  );
}
